import React, { useState, useRef, useEffect, useCallback } from 'react';
import { 
  Video,
  VideoOff,
  Mic,
  MicOff,
  Phone,
  MessageSquare,
  Clock,
  Shield,
  Loader2,
  CheckCircle,
  XCircle,
  Send,
  AlertTriangle,
  Camera,
  Volume2
} from 'lucide-react';
import { RealtimeKitProvider, useRealtimeKitClient } from '@cloudflare/realtimekit-react';
import { RtkMeeting } from '@cloudflare/realtimekit-react-ui';

interface RealtimeKitClientConsultationProps {
  roomId?: string;
  clientName?: string;
  requestId?: string;
  onLeaveConsultation?: () => void;
}

interface ChatMessage {
  id: string;
  sender: 'attorney' | 'client' | 'system';
  message: string;
  timestamp: Date;
  type: 'text' | 'file' | 'system';
}

// Generate token using the working debug endpoint (same as universal template)
const generateToken = async (roomId: string, participantName: string, isHost: boolean = false, maxRetries: number = 3) => {
  console.log(`[Legal] Generating token for room ${roomId}, participant ${participantName}, host: ${isHost}`);
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      // Use the same working debug endpoint as universal template
      const endpoint = 'https://dev-platform-api-dev.benjiemalinao879557.workers.dev/client/v1/debug/generate-token';
      const body = { meetingId: roomId, participantName, isHost };
      
      console.log(`[Legal] Using debug endpoint: ${endpoint}`, body);
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`Token generation failed: ${response.status}`);
      }

      const tokenData = await response.json();
      console.log(`[Legal] Token generated successfully for ${participantName}${attempt > 1 ? ` (attempt ${attempt})` : ''}`, tokenData);
      
      return tokenData.token;
    } catch (error) {
      if (attempt === maxRetries) {
        console.error('[Legal] Token generation error (final attempt):', error);
        throw error;
      }
      
      console.warn(`[Legal] Token generation attempt ${attempt} failed, retrying...`, error);
      await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
    }
  }
};

// Inner component that uses RealtimeKit hooks
const RealtimeKitMeetingComponent: React.FC<{
  authToken: string;
  onEndCall: () => void;
  clientName: string;
  roomId: string;
}> = ({ authToken, onEndCall, clientName, roomId }) => {
  const [meeting, initMeeting] = useRealtimeKitClient();
  const [isInitialized, setIsInitialized] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [consultationTime, setConsultationTime] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      sender: 'system',
      message: 'Welcome to your legal consultation. Your attorney will join shortly.',
      timestamp: new Date(),
      type: 'system'
    }
  ]);

  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize meeting
  useEffect(() => {
    const init = async () => {
      try {
        console.log('[VideoMeetingComponent] Initializing with authToken:', authToken ? 'exists' : 'none');
        
        await initMeeting({
          authToken: authToken,
          defaults: {
            audio: true,
            video: true,
          },
        });
        
        setIsInitialized(true);
        console.log('[VideoMeetingComponent] Meeting initialized successfully');
        
      } catch (error) {
        console.error('[VideoMeetingComponent] Failed to initialize meeting:', error);
        alert(`Connection failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\nPlease refresh the page and try again.`);
      }
    };

    if (authToken && !isInitialized) {
      init();
    }
  }, [authToken, initMeeting, isInitialized]);

  // Timer for consultation time
  useEffect(() => {
    const interval = setInterval(() => {
      setConsultationTime(prev => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Format time
  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Send message
  const sendMessage = () => {
    if (!newMessage.trim()) return;

    const message: ChatMessage = {
      id: Date.now().toString(),
      sender: 'client',
      message: newMessage,
      timestamp: new Date(),
      type: 'text'
    };

    setChatMessages(prev => [...prev, message]);
    setNewMessage('');
  };

  // Handle end call
  const handleEndCall = useCallback(async () => {
    console.log('[VideoMeetingComponent] End call requested, performing cleanup...');
    
    if (meeting && isInitialized) {
      try {
        await meeting.leave();
        console.log('[VideoMeetingComponent] Successfully left video meeting');
      } catch (error) {
        console.warn('[VideoMeetingComponent] Error leaving meeting:', error);
      }
    }
    
    onEndCall();
  }, [meeting, isInitialized, onEndCall]);

  if (!meeting || !isInitialized) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Legal Consultation</h1>
          <h2 className="text-blue-200 text-xl mb-4">Client Portal</h2>
          <p className="text-gray-300 text-lg">Connecting to your attorney...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      {/* Header Bar */}
      <div className="bg-gray-800 text-white px-6 py-3 flex items-center justify-between border-b border-gray-700">
        <div className="flex items-center space-x-4">
          <div>
            <h2 className="font-semibold">Legal Consultation</h2>
            <p className="text-sm text-gray-300">Client: {clientName}</p>
          </div>
          <div className="bg-green-900 text-green-200 px-3 py-1 rounded-full text-xs flex items-center">
            <Shield size={12} className="mr-1" />
            Secure & Confidential
          </div>
        </div>

        <div className="flex items-center space-x-6">
          {/* Timer */}
          <div className="flex items-center text-lg font-mono">
            <Clock size={16} className="mr-2" />
            {formatTime(consultationTime)}
          </div>

          {/* Recording Indicator */}
          {isRecording && (
            <div className="bg-red-900 text-red-200 px-3 py-1 rounded-full text-xs flex items-center">
              <AlertTriangle size={12} className="mr-2" />
              Recording in Progress
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Main Video Area with RealtimeKit */}
        <div className="flex-1 relative bg-black">
          <div className="h-full w-full relative">
            <RtkMeeting 
              meeting={meeting} 
              showSetupScreen={false}
              mode="fill"
            />
            {/* Overlay to hide any built-in controls at the bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-black opacity-75 pointer-events-none"></div>
          </div>

          {/* Controls Overlay - Limited for Client */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2">
            <div className="bg-gray-800 bg-opacity-90 rounded-full px-4 py-2 flex items-center space-x-3">
              {/* Chat */}
              <button
                onClick={() => setShowChat(!showChat)}
                className={`p-2 rounded-full transition-colors ${
                  showChat 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-gray-700 hover:bg-gray-600'
                }`}
              >
                <MessageSquare size={18} />
              </button>

              {/* End Call */}
              <button
                onClick={handleEndCall}
                className="p-2 rounded-full bg-red-600 hover:bg-red-700 transition-colors"
              >
                <Phone size={18} />
              </button>
            </div>
          </div>

          {/* Meeting info overlay */}
          <div className="absolute top-4 left-4 z-50 bg-black bg-opacity-50 text-white p-3 rounded-lg">
            <h3 className="font-semibold">{clientName}</h3>
            <p className="text-sm text-gray-300">Legal Consultation</p>
            <p className="text-xs text-gray-400 mt-1">Participant • Connected</p>
          </div>
        </div>

        {/* Chat Panel */}
        {showChat && (
          <div className="w-80 bg-white flex flex-col">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-900">Chat</h3>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {chatMessages.map((msg) => (
                <div
                  key={msg.id}
                  className={`${
                    msg.sender === 'system' 
                      ? 'text-center' 
                      : msg.sender === 'client' 
                        ? 'ml-auto' 
                        : 'mr-auto'
                  }`}
                >
                  {msg.sender === 'system' ? (
                    <div className="bg-blue-50 text-blue-800 text-xs px-3 py-2 rounded-lg">
                      {msg.message}
                    </div>
                  ) : (
                    <div
                      className={`max-w-xs px-3 py-2 rounded-lg text-sm ${
                        msg.sender === 'client'
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-900'
                      }`}
                    >
                      <p>{msg.message}</p>
                      <p className="text-xs opacity-75 mt-1">
                        {msg.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-gray-200 p-4">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Type a message..."
                />
                <button
                  onClick={sendMessage}
                  className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Send size={16} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Recording Consent Modal */}
      {!recordingConsent && isRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="text-amber-500 mr-3" size={24} />
              <h3 className="text-lg font-semibold">Recording Consent Required</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Your attorney would like to record this consultation for your case file. 
              The recording will be kept confidential under attorney-client privilege.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setRecordingConsent(true)}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
              >
                I Consent
              </button>
              <button
                onClick={() => setRecordingConsent(false)}
                className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors"
              >
                No Recording
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const RealtimeKitClientConsultation: React.FC<RealtimeKitClientConsultationProps> = ({
  roomId: initialRoomId,
  clientName = 'Client',
  requestId,
  onLeaveConsultation
}) => {
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const roomId = initialRoomId || urlParams.get('roomId') || '';
  const clientNameFromUrl = urlParams.get('clientName') || clientName;
  const requestIdFromUrl = urlParams.get('requestId') || requestId || '';
  const debugToken = urlParams.get('authToken'); // Get authToken from URL if provided (only for debug mode)
  const isDebugMode = urlParams.get('debug') === 'true';
  // Ignore the old 'token' parameter - we'll generate real tokens

  const [isInitializing, setIsInitializing] = useState(true);
  const [authToken, setAuthToken] = useState<string | null>(debugToken || null);
  const [error, setError] = useState<string | null>(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(true);
  const [permissionsGranted, setPermissionsGranted] = useState(false);
  const [devicesChecked, setDevicesChecked] = useState(false);
  const [showEndedMessage, setShowEndedMessage] = useState(false);

  const localVideoRef = useRef<HTMLVideoElement>(null);

  // Check device permissions
  const checkDevicePermissions = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });
      
      // Test devices work
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      setPermissionsGranted(true);
      setDevicesChecked(true);
      
      // Stop test stream
      setTimeout(() => {
        stream.getTracks().forEach(track => track.stop());
      }, 3000);
      
    } catch (error) {
      console.error('Failed to get device permissions:', error);
      setPermissionsGranted(false);
      setDevicesChecked(true);
    }
  };

  // Initialize client token when permissions are granted
  useEffect(() => {
    const initializeClientCall = async () => {
      if (!roomId || (!permissionsGranted && !isDebugMode) || (!devicesChecked && !isDebugMode)) {
        return;
      }

      try {
        console.log('[VideoClientConsultation] Starting client call initialization for room:', roomId);
        
        // Check if we already have a token (from debug mode)
        if (debugToken && isDebugMode) {
          console.log('[VideoClientConsultation] Using debug token from URL');
          setIsInitializing(false);
          setShowPermissionsModal(false);
        } else {
          // Generate token using retry-enabled function (same as universal template)
          console.log('[VideoClientConsultation] Generating real token from debug endpoint');
          try {
            const token = await generateToken(roomId, clientNameFromUrl, false);
            setAuthToken(token);
            console.log('[VideoClientConsultation] Successfully generated real token');
          } catch (debugError) {
            console.error('[VideoClientConsultation] Token generation failed completely:', debugError);
            setError(`Failed to generate video token: ${debugError instanceof Error ? debugError.message : 'Unknown error'}`);
            setIsInitializing(false);
            return;
          }
          
          setIsInitializing(false);
          setShowPermissionsModal(false);
        }

      } catch (error) {
        console.error('[VideoClientConsultation] Failed to initialize client call:', error);
        setError(error instanceof Error ? error.message : 'Failed to join consultation');
        setIsInitializing(false);
      }
    };

    initializeClientCall();
  }, [roomId, clientNameFromUrl, permissionsGranted, devicesChecked, debugToken, isDebugMode]);

  // Handle end call
  const handleEndCall = async () => {
    try {
      console.log('[VideoClientConsultation] Ending call cleanup...');
      
      setShowEndedMessage(true);
      
    } catch (error) {
      console.error('[VideoClientConsultation] Error during call cleanup:', error);
    }
  };

  // Show ended message
  if (showEndedMessage) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Consultation Ended</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your consultation. Your attorney will follow up with you soon.
          </p>
          <button
            onClick={() => onLeaveConsultation ? onLeaveConsultation() : window.close()}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Show permissions modal
  if (showPermissionsModal && isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-8 max-w-lg w-full">
          <div className="text-center mb-6">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Legal Video Consultation</h2>
            <p className="text-gray-600">Room ID: {roomId}</p>
          </div>

          {!devicesChecked && (
            <div className="mb-6">
              <h3 className="font-semibold text-gray-900 mb-3">Before we begin:</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <Camera className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Camera Access</p>
                    <p className="text-xs text-gray-600">We need access to your camera for video</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <Volume2 className="w-5 h-5 text-gray-400 mr-3 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Microphone Access</p>
                    <p className="text-xs text-gray-600">We need access to your microphone for audio</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {devicesChecked && !permissionsGranted && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <XCircle className="w-5 h-5 text-red-500 mr-3" />
                <div>
                  <p className="text-sm font-medium text-red-900">Permissions Required</p>
                  <p className="text-xs text-red-700 mt-1">
                    Please allow camera and microphone access to join the consultation.
                  </p>
                </div>
              </div>
            </div>
          )}

          {devicesChecked && permissionsGranted && (
            <>
              <div className="mb-6">
                <div className="bg-gray-900 rounded-lg overflow-hidden h-48 relative">
                  <video
                    ref={localVideoRef}
                    className="w-full h-full object-cover"
                    autoPlay
                    muted
                    playsInline
                  />
                  <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    Device Test
                  </div>
                </div>
              </div>
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Devices Ready</p>
                    <p className="text-xs text-green-700 mt-1">
                      Your camera and microphone are working properly.
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          {!devicesChecked ? (
            <button
              onClick={checkDevicePermissions}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Check Devices
            </button>
          ) : isInitializing ? (
            <div className="flex items-center justify-center py-3">
              <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
              <span className="ml-2 text-gray-600">Connecting to consultation...</span>
            </div>
          ) : (
            <button
              disabled={!permissionsGranted}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Joining Consultation...
            </button>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Loading state
  if (isInitializing || !authToken) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-blue-900 via-gray-900 to-indigo-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-white/20 border-t-white mx-auto mb-6" />
          <h1 className="text-white text-3xl font-bold mb-2">Legal Consultation</h1>
          <h2 className="text-blue-200 text-xl mb-4">Client Portal</h2>
          <p className="text-gray-300 text-lg">Joining consultation...</p>
          <p className="text-gray-400 text-sm mt-4">Room ID: {roomId}</p>
          {error && (
            <div className="mt-4 p-3 bg-red-900/50 border border-red-500 rounded-lg">
              <p className="text-red-200 text-sm">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-red-900 via-gray-900 to-red-900 z-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-white text-xl mb-2">Consultation Error</h2>
          <p className="text-gray-400 mb-4">{error}</p>
          <button
            onClick={() => window.close()}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Render the RealtimeKit meeting
  return (
    <RealtimeKitMeetingComponent
      authToken={authToken}
      onEndCall={handleEndCall}
      clientName={clientNameFromUrl}
      roomId={roomId}
    />
  );
};